import React, {MouseEvent, MutableRefObject, useCallback, useState} from 'react';
import intl from "react-intl-universal";

import {useAppState, useAuthService} from "@stores/AppStateContext";

import {Button} from "../../../../components/Button";
import {AWSAPIError} from "../../../../../utils/types";
import {MODALS} from "../../modal-names";
import {useNotification} from "../../../../hooks/useNotification";

export interface ResendConfirmationCodeProps {
    setErrors: ({}) => void;
    getEmail: () => string;
    emailFieldRef?: MutableRefObject<HTMLInputElement | undefined>;
}

export const ResendForgetPasswordCode = (
    {
        setErrors,
        getEmail,
        emailFieldRef,
    }: ResendConfirmationCodeProps) =>
{
    const appState = useAppState();
    const {enqueueSnackbar} = useNotification();
    const authService = useAuthService();
    const [isProcessingResend, setIsProcessingResend] = useState(false);

    const handleResendClick = useCallback((event: MouseEvent) => {
        event.preventDefault();
        if (isProcessingResend) {
            return;
        }
        setErrors({});
        setIsProcessingResend(true);

        let username = getEmail();

        if (!username) {
            setErrors({
                confirm_code: intl.get('sign_up.email_required').d('Email is required'),
            });
            emailFieldRef?.current?.focus();
            setIsProcessingResend(false);
            return;
        }

        authService.resetPassword(username)
            .then(() => {
                setIsProcessingResend(false);
                enqueueSnackbar({
                    message: intl.get('sign_up.confirm_resent_success').d('We have sent you a confirmation code. Please check your email'),
                    variant: 'success',
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'right',
                    }
                });
            })
            .catch((error: any) => {
                setIsProcessingResend(false);

                switch ((error as AWSAPIError).name) {
                    case 'LimitExceededException': {
                        appState.setModal({name: MODALS.LOCKED, params:{
                            reason: 'pwd_reset_resend_limit'
                        }});
                        break;
                    }
                    default:
                        setErrors({
                            confirm_code: (error as Error).message,
                        });
                }
            });
    }, [isProcessingResend]);

    return (
        <Button
            className={'Auth__inline-btn'}
            onClick={handleResendClick}
            text={intl.get('sign_up.resend_btn').d('Resend')}
            style={'plain'}
            type={'primary'}
            loading={isProcessingResend}
            size={'sm'}
        />
    )
}
